import {useMemo} from "react";
import cn from "classnames";

import {Container} from "@pg-design/grid-module";
import {type ITextProps, Text} from "@pg-design/text-module";

import type {IFaq} from "../types/IFaq";
import type {IFaqQuestion} from "../types/IFaqQuestion";
import {FaqQuestion} from "./FaqQuestion";
import {FaqSchema} from "./FaqSchema";

import * as styles from "./FaqSection.module.css";

interface IProps {
    className?: string;
    faqContent: IFaq[];
    genericFaqTitle?: string;
    genericQuestions?: IFaqQuestion[] | null;
    titleAlign?: ITextProps["align"];
    headlineAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

const FAQ_TITLE = "Często zadawane pytania";

export const FaqSection = (props: IProps) => {
    if (props.faqContent.length > 0 || (props.genericQuestions && !!props.genericQuestions.length)) {
        const schemaQuestions = useMemo(
            () =>
                props.faqContent
                    .reduce((acc, curr) => [...acc, ...curr.questions], [] as IFaqQuestion[])
                    .concat(props.genericQuestions && !!props.genericQuestions.length ? props.genericQuestions : []),
            [props.faqContent, props.genericQuestions]
        );
        const indexedTitles = useMemo(() => props.faqContent.reduce((acc, curr) => [...acc, curr.title], [] as string[]), [props.faqContent]);
        const addGenericToSection = useMemo(
            () => indexedTitles.findIndex((title) => title.trim() === FAQ_TITLE || title.trim() === props.genericFaqTitle?.trim()),
            [indexedTitles, props.genericFaqTitle]
        );
        const addGenericFaqSection = props.genericQuestions && props.genericQuestions.length > 0 && addGenericToSection < 0;

        return (
            <>
                {addGenericFaqSection && (
                    <Container className={cn(styles.wrapper, props.className)}>
                        <Text as={props.headlineAs || "h2"} className={styles.title} align={props.titleAlign} variant="headline_3">
                            {props.genericFaqTitle || FAQ_TITLE}
                        </Text>

                        <div className={styles.list}>
                            {props.genericQuestions?.map((faqElement) => {
                                return <FaqQuestion key={faqElement.name} {...faqElement} />;
                            })}
                        </div>
                    </Container>
                )}

                {props.faqContent.map((faq, index) => {
                    const title = faq.title || (index === 0 && props.genericFaqTitle) || FAQ_TITLE;

                    return (
                        <Container key={title} className={cn(styles.wrapper, props.className)}>
                            <Text as={props.headlineAs || "h2"} className={styles.title} align={props.titleAlign} variant="headline_3">
                                {title}
                            </Text>

                            <div className={styles.list}>
                                {props.genericQuestions &&
                                    index === addGenericToSection &&
                                    props.genericQuestions.map((faqElement) => {
                                        return <FaqQuestion key={faqElement.name} {...faqElement} />;
                                    })}
                                {faq.questions.map((faqElement) => {
                                    return <FaqQuestion key={faqElement.name} {...faqElement} />;
                                })}
                            </div>
                        </Container>
                    );
                })}

                <FaqSchema questions={schemaQuestions} />
            </>
        );
    }

    return <></>;
};
